/**
 * CorrespondentDocumentsCoverage — the correspondent drilldown's Agreements tab,
 * laid out like the loan Documents tab's "Document Group Coverage" view
 * (LoanDrilldownScreen/tabs/LoanDrilldownDocumentsTab/DocGroupCoverageView.js).
 *
 * TAXONOMY-first: every correspondent document category is listed, grouped,
 * whether or not a file exists, so a gap is as visible as what's on file. A
 * category with files expands to show them; an empty one offers an upload.
 *
 * Data: `detail.documents` (already on the correspondent record — no extra
 * fetch). Categories are DOCUMENT_CATEGORIES (verbatim from the backend's
 * CORRESPONDENT_DOC_CATEGORIES); a document whose category isn't in that list
 * is shown under Other rather than dropped, so the file count always matches
 * the record.
 *
 * The four AGREEMENT categories keep the version-control features the tab had:
 * version number, "Current" (agreement of record), review status + the
 * set-status menu (Final Executed promotes and stamps mlpaSigned), stage,
 * download, and the version compare (diff). Every upload — a row's Upload or
 * the header's "Upload version" — opens CorrespondentUploadPanel (the row's
 * category preselected), which asks for the stage / superseded version on an
 * agreement category.
 *
 * There's no delete: the backend has no route to remove a correspondent
 * document.
 *
 * The loan view's TypeRow / FileRow aren't imported: they read loan-only fields
 * (documentStatus, docType validity, the loan upload queue). This mirrors their
 * look with the shared tokens instead.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { AgGridReact } from "ag-grid-react";
import { ModuleRegistry } from "ag-grid-community";
import { RowGroupingModule } from "ag-grid-enterprise";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import ButtonBase from "@mui/material/ButtonBase";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import LinearProgress from "@mui/material/LinearProgress";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Tooltip from "@mui/material/Tooltip";

import ArrowUpwardRounded from "@mui/icons-material/ArrowUpwardRounded";
import CheckRounded from "@mui/icons-material/CheckRounded";
import CloseRounded from "@mui/icons-material/CloseRounded";
import CompareArrowsRounded from "@mui/icons-material/CompareArrowsRounded";
import FileDownloadOutlined from "@mui/icons-material/FileDownloadOutlined";
import InfoOutlined from "@mui/icons-material/InfoOutlined";
import KeyboardArrowDownRounded from "@mui/icons-material/KeyboardArrowDownRounded";
import OpenInNewRounded from "@mui/icons-material/OpenInNewRounded";
import SearchOutlined from "@mui/icons-material/SearchOutlined";
import UploadFileOutlined from "@mui/icons-material/UploadFileOutlined";
import CloudUploadOutlined from "@mui/icons-material/CloudUploadOutlined";
import VisibilityOutlined from "@mui/icons-material/VisibilityOutlined";

import AgGridTableWrapper from "../../../components/agGrid/AgGridTableWrapper";
import Flx from "../../../components/layout/Flx";
import { segmentedGroupSx } from "../../../theme/activeStates";
import { ActionCell, actionColumn } from "../../admin/shared/adminTableActions";
import { formatDateTime } from "../../../_src_shared/utils/functions/formatDateTime";
import {
  AGREEMENT_CATEGORIES,
  AGREEMENT_STATUSES,
  DOCUMENT_CATEGORIES,
  ONBOARDING_BLANK_FORMS_URL,
  docId,
  oidOf,
  useCorrespondentsApi,
} from "../../../hooks/useCorrespondentsHook";
import { slate, status as statusColors, teal } from "../../../theme/colors";
import {
  EMPTY_DOT,
  Highlight,
  INK_2,
  INK_3,
  LINE,
  LINE_SOFT,
} from "../../LoanDrilldownScreen/shared/docGroupCoverage";
import AgreementDocumentModal from "./AgreementDocumentModal";
import { fmtBytes } from "./CorrespondentFileSlot";
import { CorrespondentUploadPanel } from "./CorrespondentPanelForms";
import { SoftBadge, StatusChip } from "./correspondentUi";

// The table view groups by type (row grouping is an enterprise module).
ModuleRegistry.registerModules([RowGroupingModule]);

/* ───────────────────────────── taxonomy ───────────────────────────── */

// The groups the categories are shown in. The backend has no grouping, so this
// is the FE's; every DOCUMENT_CATEGORIES value appears exactly once (checked
// below — anything unlisted falls into Other).
const GROUPS = [
  {
    name: "Agreements",
    desc: "Versioned — each upload is a new version; one is the agreement of record.",
    categories: [
      "Mortgage Loan Purchase Agreement",
      "Asset Management Agreement",
      "Loan Funding Agreement",
      "Broker Agreement",
    ],
  },
  {
    name: "Onboarding",
    categories: [
      "Non-Disclosure Agreement",
      "Prospect Questionnaire",
      "Onboarding Checklist",
      "Background Authorization",
    ],
  },
  {
    name: "Company & compliance",
    categories: ["W9", "Wiring Instructions", "Corporate Docs", "Licensing", "Insurance"],
  },
  { name: "Other", categories: ["Other"] },
];
const GROUPED = new Set(GROUPS.flatMap((g) => g.categories));

// Legacy uploads used the abbreviation "MLPA" — same lineage as the full name
// (MLPA_CATEGORY_ALIASES on the backend).
const CATEGORY_ALIASES = { MLPA: "Mortgage Loan Purchase Agreement" };
const canonCategory = (c) => CATEGORY_ALIASES[c] || c;

const isAgreementCategory = (c) => AGREEMENT_CATEGORIES.includes(c);

const nameOf = (d) => d?.file_display_name || d?.file_name_as_loaded || "Untitled";

const byNewest = (a, b) => {
  const va = a.agreementVersion || 0;
  const vb = b.agreementVersion || 0;
  if (va !== vb) return vb - va;
  return String(b.uploadDate || "").localeCompare(String(a.uploadDate || ""));
};

const CARD_SHADOW = "0 1px 2px rgba(15,23,42,.05)";

/* ───────────────────────────── small pieces ───────────────────────────── */

// Row-action pills — dashed on an empty category (revealed on hover), solid
// on one with files. Same recipe as the loan coverage TypeRow.
const pillSx = (dashed) => ({
  flex: "none",
  display: "inline-flex",
  alignItems: "center",
  gap: 0.5,
  border: `1px ${dashed ? "dashed" : "solid"} ${teal[200]}`,
  background: "#fff",
  color: teal[700],
  fontFamily: "inherit",
  fontSize: 11.5,
  fontWeight: 600,
  borderRadius: "6px",
  px: 1.125,
  py: "3px",
  "&:hover": { background: teal[100], borderStyle: "solid" },
});

const smallIconSx = { color: INK_3, "&:hover": { color: slate[700] } };

/** Review-status picker for one agreement version. */
function StatusMenuButton({ doc, onPick }) {
  const [anchor, setAnchor] = useState(null);
  return (
    <>
      <Tooltip title="Set review status">
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            setAnchor(e.currentTarget);
          }}
          aria-label="Set review status"
          sx={smallIconSx}
        >
          <CheckRounded sx={{ fontSize: 17 }} />
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={anchor}
        open={!!anchor}
        onClose={() => setAnchor(null)}
        slotProps={{ paper: { sx: { minWidth: 190 } } }}
      >
        {AGREEMENT_STATUSES.map((s) => (
          <MenuItem
            key={s}
            selected={doc?.status === s}
            onClick={() => {
              setAnchor(null);
              onPick(doc, s);
            }}
            sx={{ fontSize: 13 }}
          >
            {s}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}

/** One uploaded file under a category. */
function FileLine({ doc, versioned, isCurrent, term, onPreview, onDownload, onCompare, onStatus }) {
  const meta = [
    fmtBytes(doc.size),
    doc.uploadDate ? formatDateTime(doc.uploadDate, "M/d/yy") : null,
    doc.uploadedBy || null,
  ]
    .filter(Boolean)
    .join(" · ");
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        px: 1,
        py: 1,
        borderRadius: "8px",
        "&:hover": { background: LINE_SOFT },
      }}
    >
      {versioned && (
        <Box sx={{ flex: "none", minWidth: 30 }}>
          <SoftBadge tone="slate">
            {doc.agreementVersion ? `v${doc.agreementVersion}` : "—"}
          </SoftBadge>
        </Box>
      )}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Flx ac g={0.75} sx={{ minWidth: 0 }}>
          <Box
            component="button"
            type="button"
            onClick={() => onPreview(doc)}
            sx={{
              minWidth: 0,
              border: "none",
              background: "none",
              p: 0,
              font: "inherit",
              fontSize: 13.5,
              fontWeight: 500,
              color: slate[900],
              textAlign: "left",
              cursor: "pointer",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              "&:hover": { color: teal[700], textDecoration: "underline" },
            }}
          >
            <Highlight text={nameOf(doc)} term={term} />
          </Box>
          {isCurrent && (
            <Box sx={{ flex: "none" }}>
              <SoftBadge tone="green" icon={CheckRounded}>
                Current
              </SoftBadge>
            </Box>
          )}
        </Flx>
        <Box sx={{ mt: "3px", fontSize: 12, color: slate[500], lineHeight: 1.4 }}>
          {meta}
          {versioned && doc.agreementStage ? ` · ${doc.agreementStage}` : ""}
        </Box>
      </Box>
      {versioned && doc.status && (
        <Box sx={{ flex: "none" }}>
          <StatusChip status={doc.status} />
        </Box>
      )}
      <Tooltip title="Preview">
        <IconButton size="small" aria-label="Preview" onClick={() => onPreview(doc)} sx={smallIconSx}>
          <VisibilityOutlined sx={{ fontSize: 17 }} />
        </IconButton>
      </Tooltip>
      <Tooltip title="Download">
        <IconButton size="small" aria-label="Download" onClick={() => onDownload(doc)} sx={smallIconSx}>
          <FileDownloadOutlined sx={{ fontSize: 17 }} />
        </IconButton>
      </Tooltip>
      {versioned && (
        <Tooltip title="Compare against this version">
          <IconButton size="small" aria-label="Compare" onClick={() => onCompare(doc)} sx={smallIconSx}>
            <CompareArrowsRounded sx={{ fontSize: 17 }} />
          </IconButton>
        </Tooltip>
      )}
      {versioned && <StatusMenuButton doc={doc} onPick={onStatus} />}
    </Box>
  );
}

/** Version compare for one agreement category (GET /agreements/diff). */
function ComparePanel({ cid, category, versions, from, onFromChange, onClose }) {
  const correspondentsApi = useCorrespondentsApi();
  const [to, setTo] = useState("");
  const [diff, setDiff] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const run = async () => {
    setBusy(true);
    setError(null);
    try {
      setDiff(
        await correspondentsApi.agreementDiff(cid, {
          fromDocId: from || undefined,
          toDocId: to || undefined,
          category,
        }),
      );
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "Diff failed");
    } finally {
      setBusy(false);
    }
  };

  const select = (label, value, onChange, blank) => (
    <Box sx={{ flex: 1, minWidth: 160 }}>
      <Box sx={{ mb: 0.5, fontSize: 11.5, color: INK_2 }}>{label}</Box>
      <TextField
        select
        size="small"
        fullWidth
        value={value}
        onChange={(e) => onChange(e.target.value)}
        sx={{ "& .MuiOutlinedInput-root": { fontSize: 12.5, background: "#fff" } }}
      >
        <MenuItem value="" sx={{ fontSize: 12.5 }}>
          {blank}
        </MenuItem>
        {versions.map((v) => (
          <MenuItem key={oidOf(v)} value={oidOf(v)} sx={{ fontSize: 12.5 }}>
            v{v.agreementVersion ?? "—"} · {nameOf(v)}
          </MenuItem>
        ))}
      </TextField>
    </Box>
  );

  return (
    <Box
      sx={{
        mt: 1,
        border: `1px solid ${LINE}`,
        borderRadius: "8px",
        background: slate[50],
        p: 1.25,
      }}
    >
      <Flx ac jb sx={{ mb: 1 }}>
        <Flx ac g={0.75}>
          <CompareArrowsRounded sx={{ fontSize: 16, color: INK_2 }} />
          <Box sx={{ fontSize: 12.5, fontWeight: 600, color: slate[900] }}>
            Compare versions
          </Box>
        </Flx>
        <IconButton size="small" aria-label="Close compare" onClick={onClose} sx={smallIconSx}>
          <CloseRounded sx={{ fontSize: 16 }} />
        </IconButton>
      </Flx>
      <Flx g={1.25} wrap sx={{ alignItems: "flex-end" }}>
        {select("From version", from, onFromChange, "— Version it supersedes —")}
        {select("To version", to, setTo, "— Latest —")}
        <Button
          variant="contained"
          size="small"
          onClick={run}
          disabled={busy || versions.length < 2}
          sx={{ flexShrink: 0, height: 40 }}
        >
          {busy ? "Comparing…" : "Run diff"}
        </Button>
      </Flx>
      {error && (
        <Box sx={{ mt: 1, fontSize: 12, color: statusColors.error.fg }}>{error}</Box>
      )}
      {diff && !diff.diffAvailable && (
        <Flx
          g={1}
          sx={{
            mt: 1.25,
            alignItems: "flex-start",
            borderRadius: "8px",
            background: statusColors.warning.bg,
            color: statusColors.warning.fg,
            px: 1.5,
            py: 1,
            fontSize: 12,
          }}
        >
          <InfoOutlined sx={{ fontSize: 16, mt: "1px" }} />
          <Box>
            No diff yet for v{diff.fromVersion ?? "—"} → v{diff.toVersion ?? "—"}.{" "}
            {diff.reason ||
              "The extraction pipeline has not produced text for both versions."}
          </Box>
        </Flx>
      )}
      {diff?.diffAvailable && (
        <Box sx={{ mt: 1.25 }}>
          <Box sx={{ mb: 0.75, fontSize: 12, color: INK_2 }}>
            v{diff.fromVersion} → v{diff.toVersion}
          </Box>
          <Box
            component="pre"
            sx={{
              m: 0,
              maxHeight: 420,
              overflow: "auto",
              borderRadius: "8px",
              background: slate[900],
              color: slate[200],
              px: 1.5,
              py: 1.25,
              fontSize: 12,
              lineHeight: 1.6,
              whiteSpace: "pre",
              fontFamily: "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace",
            }}
          >
            {diff.unifiedDiff}
          </Box>
        </Box>
      )}
    </Box>
  );
}

/** One category row (+ its files when open). */
function CategoryRow({
  cid,
  category,
  files,
  term,
  open,
  onToggle,
  onUpload,
  uploading,
  ...fileHandlers
}) {
  const has = files.length > 0;
  const versioned = isAgreementCategory(category);
  const [compareFrom, setCompareFrom] = useState(null); // null = closed
  const currentOid = oidOf(files.find((d) => d.isCurrentAgreement));

  return (
    <Box>
      <Box
        role={has ? "button" : undefined}
        tabIndex={has ? 0 : undefined}
        aria-expanded={has ? open : undefined}
        onClick={has ? onToggle : undefined}
        onKeyDown={
          has
            ? (e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  onToggle();
                }
              }
            : undefined
        }
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.125,
          px: 1,
          py: 0.875,
          borderRadius: "7px",
          cursor: has ? "pointer" : "default",
          outline: "none",
          "&:hover": { background: has ? teal[50] : LINE_SOFT },
          "&:focus-visible": { boxShadow: `0 0 0 2px ${teal[300]}` },
          "&:hover .cov-upload, &:focus-within .cov-upload": { display: "inline-flex" },
        }}
      >
        <Box
          sx={{
            width: 7,
            height: 7,
            borderRadius: "50%",
            flex: "none",
            background: has ? teal[500] : EMPTY_DOT,
          }}
        />
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            fontSize: 12.5,
            color: has ? slate[900] : INK_3,
            fontWeight: has ? 500 : 400,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          <Highlight text={category} term={term} />
        </Box>
        {versioned && currentOid && (
          <Tooltip title="An agreement of record is on file">
            <CheckRounded sx={{ fontSize: 16, color: statusColors.success.solid, flex: "none" }} />
          </Tooltip>
        )}
        {has && (
          <Box
            component="span"
            sx={{
              flex: "none",
              fontSize: 11,
              fontWeight: 700,
              color: teal[700],
              background: teal[100],
              borderRadius: "999px",
              px: 1,
              py: "1px",
            }}
          >
            {files.length}
          </Box>
        )}
        <ButtonBase
          className="cov-upload"
          onClick={(e) => {
            e.stopPropagation();
            onUpload(category);
          }}
          sx={{
            ...pillSx(!has),
            // Empty → revealed on hover; with files → shown while open.
            display: has && open ? "inline-flex" : "none",
          }}
        >
          <ArrowUpwardRounded sx={{ fontSize: 12 }} />
          {versioned ? (has ? "New version" : "Upload") : "Upload"}
        </ButtonBase>
        {has && (
          <KeyboardArrowDownRounded
            sx={{
              fontSize: 15,
              color: INK_3,
              flex: "none",
              transition: "transform .15s",
              transform: open ? "rotate(180deg)" : "none",
            }}
          />
        )}
      </Box>

      {uploading && (
        <Box sx={{ ml: 3, mr: 1, mb: 0.75, pl: 1.25 }}>
          <Box sx={{ fontSize: 11.5, color: INK_2 }}>
            Uploading {uploading.name}… {fmtBytes(uploading.size)}
          </Box>
          <LinearProgress
            variant={uploading.pct == null ? "indeterminate" : "determinate"}
            value={uploading.pct ?? 0}
            sx={{
              mt: 0.5,
              height: 4,
              borderRadius: 2,
              backgroundColor: slate[200],
              "& .MuiLinearProgress-bar": { backgroundColor: teal[600], borderRadius: 2 },
            }}
          />
        </Box>
      )}

      {has && open && (
        <Box sx={{ mt: "2px", mb: 0.75, mr: 1, ml: 3, borderLeft: `2px solid ${LINE_SOFT}`, pl: 1.25 }}>
          {files.map((doc) => (
            <FileLine
              key={oidOf(doc)}
              doc={doc}
              versioned={versioned}
              isCurrent={versioned && oidOf(doc) === currentOid}
              term={term}
              onCompare={(d) => setCompareFrom(oidOf(d))}
              {...fileHandlers}
            />
          ))}
          {versioned && compareFrom === null && files.length > 1 && (
            <ButtonBase onClick={() => setCompareFrom("")} sx={{ ...pillSx(false), mt: 0.75 }}>
              <CompareArrowsRounded sx={{ fontSize: 13 }} />
              Compare versions
            </ButtonBase>
          )}
          {versioned && compareFrom !== null && (
            <ComparePanel
              cid={cid}
              category={category}
              versions={files}
              from={compareFrom}
              onFromChange={setCompareFrom}
              onClose={() => setCompareFrom(null)}
            />
          )}
        </Box>
      )}
    </Box>
  );
}

/** One group card. */
function GroupCard({ group, rows, term, collapsed, onToggleCollapsed, renderRow }) {
  const withDocs = rows.filter((r) => r.files.length).length;
  const pct = rows.length ? Math.round((withDocs / rows.length) * 100) : 0;
  const hasAny = withDocs > 0;
  return (
    <Box
      sx={{
        breakInside: "avoid",
        background: "#fff",
        border: `1px solid ${LINE}`,
        borderRadius: "10px",
        boxShadow: CARD_SHADOW,
        mb: 1.75,
        overflow: "hidden",
      }}
    >
      <Box
        role="button"
        tabIndex={0}
        aria-expanded={!collapsed}
        onClick={onToggleCollapsed}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onToggleCollapsed();
          }
        }}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.25,
          px: 1.75,
          py: 1.375,
          cursor: "pointer",
          userSelect: "none",
          outline: "none",
          "&:hover": { background: slate[50] },
          "&:focus-visible": { boxShadow: `inset 0 0 0 2px ${teal[300]}` },
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ fontSize: 13.5, fontWeight: 600, color: slate[900] }}>
            {group.name}
          </Box>
          {group.desc && (
            <Box sx={{ fontSize: 11.5, color: INK_3 }}>{group.desc}</Box>
          )}
        </Box>
        <Box
          component="span"
          sx={{
            flex: "none",
            fontSize: 11.5,
            fontWeight: 600,
            borderRadius: "999px",
            px: 1.125,
            py: "2px",
            color: hasAny ? teal[700] : INK_2,
            background: hasAny ? teal[100] : LINE_SOFT,
          }}
        >
          {withDocs} of {rows.length}
        </Box>
        <KeyboardArrowDownRounded
          sx={{
            fontSize: 18,
            color: INK_3,
            flex: "none",
            transition: "transform .15s",
            transform: collapsed ? "rotate(-90deg)" : "none",
          }}
        />
      </Box>
      {!collapsed && (
        <>
          <Box sx={{ height: 3, mx: 1.75, borderRadius: "2px", background: LINE_SOFT, overflow: "hidden" }}>
            <Box
              sx={{
                height: "100%",
                width: `${pct}%`,
                background: teal[500],
                borderRadius: "2px",
                transition: "width .3s",
              }}
            />
          </Box>
          <Box sx={{ pt: 1, pb: 1.25, px: 0.75 }}>
            {rows.length ? (
              rows.map(renderRow)
            ) : (
              <Box sx={{ fontSize: 12, color: INK_3, px: 1, py: 0.5 }}>
                No document types match the search
              </Box>
            )}
          </Box>
        </>
      )}
    </Box>
  );
}

/* ───────────────────────────── the tab ───────────────────────────── */

// Toolbar buttons match the Request Board header's: 36px tall, 8px corners,
// 14px side padding.
const HEADER_BTN = { height: 36, minHeight: 36, py: 0, px: "14px", borderRadius: "8px" };

/**
 * The drop zone above the board / table: drop or pick a file and the upload
 * sidebar opens with it already in the File field, so all that's left is the
 * category (and, for an agreement, the stage).
 */
function DropToUpload({ onFile }) {
  const [over, setOver] = useState(false);
  const inputRef = useRef(null);
  const depth = useRef(0);
  const browse = () => inputRef.current?.click();
  const drag = (delta) => (e) => {
    e.preventDefault();
    depth.current = Math.max(0, depth.current + delta);
    setOver(depth.current > 0);
  };
  return (
    <Box
      role="button"
      tabIndex={0}
      onClick={browse}
      onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && browse()}
      onDragEnter={drag(1)}
      onDragLeave={drag(-1)}
      onDragOver={(e) => e.preventDefault()}
      onDrop={(e) => {
        e.preventDefault();
        depth.current = 0;
        setOver(false);
        const f = e.dataTransfer.files?.[0];
        if (f) onFile(f);
      }}
      sx={{
        mt: 2,
        border: `2px dashed ${over ? teal[400] : slate[300]}`,
        borderRadius: "12px",
        background: over ? teal[50] : "transparent",
        px: 3,
        py: 3.5,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 0.75,
        textAlign: "center",
        cursor: "pointer",
        transition: "background-color .12s, border-color .12s",
        "&:hover": { borderColor: teal[400], background: teal[50] },
        "&:focus-visible": { outline: `2px solid ${teal[200]}`, outlineOffset: 2 },
      }}
    >
      <input
        ref={inputRef}
        type="file"
        hidden
        onChange={(e) => {
          const f = e.target.files?.[0];
          e.target.value = "";
          if (f) onFile(f);
        }}
      />
      <CloudUploadOutlined sx={{ fontSize: 34, color: slate[400] }} />
      <Box sx={{ fontSize: 14, fontWeight: 600, color: slate[900] }}>
        Drop a file here or click to browse
      </Box>
      <Box sx={{ fontSize: 12.5, color: slate[500] }}>
        The upload panel opens with the file attached — pick its document type there.
      </Box>
    </Box>
  );
}

function TableGroupRow({ node, onUpload }) {
  const category = node?.key;
  return (
    <Flx ac g={1} sx={{ height: "100%", width: "100%", pr: 1 }}>
      <Box sx={{ fontWeight: 700, fontSize: 13, color: slate[900] }}>{category}</Box>
      <Box sx={{ fontSize: 12, color: INK_3 }}>({node?.allChildrenCount ?? 0})</Box>
      <ButtonBase
        onClick={(e) => {
          e.stopPropagation();
          onUpload(category);
        }}
        sx={{ ...pillSx(false), ml: "auto" }}
      >
        <ArrowUpwardRounded sx={{ fontSize: 13 }} />
        Upload
      </ButtonBase>
    </Flx>
  );
}

/**
 * Table view — every uploaded file in one AG Grid, grouped by document type
 * (category) the way the loan Documents tab groups its table. Only files are
 * rows; the board view is where empty categories show.
 */
function DocumentsTable({ docs, term, onPreview, onDownload, onStatus, onCompare, onUpload }) {
  const rows = useMemo(
    () =>
      docs.map((d) => ({
        ...d,
        _category: canonCategory(d.category || "Other"),
        _name: nameOf(d),
      })),
    [docs],
  );

  // Handlers through a ref so the column defs stay stable (the parent's
  // callbacks are recreated every render; new defs would reset the grid).
  const h = useRef({});
  h.current = { onPreview, onDownload, onStatus, onCompare, onUpload };

  const columnDefs = useMemo(
    () => [
      { field: "_category", headerName: "Type", rowGroup: true, hide: true },
      {
        field: "_name",
        headerName: "File",
        flex: 2,
        minWidth: 240,
        // Files sit indented under their bold type row.
        cellStyle: { paddingLeft: 36 },
        cellRenderer: (p) =>
          p.data ? (
            <Flx ac sx={{ height: "100%", minWidth: 0 }}>
              <Box
                component="button"
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  h.current.onPreview(p.data);
                }}
                sx={{
                  border: "none",
                  background: "none",
                  p: 0,
                  font: "inherit",
                  fontSize: 13,
                  color: teal[700],
                  cursor: "pointer",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  "&:hover": { textDecoration: "underline" },
                }}
              >
                {p.value}
              </Box>
            </Flx>
          ) : null,
      },
      {
        field: "agreementVersion",
        headerName: "Version",
        width: 100,
        cellRenderer: (p) =>
          p.data?.agreementVersion ? (
            <Flx ac g={0.5} sx={{ height: "100%" }}>
              <SoftBadge tone="slate">v{p.data.agreementVersion}</SoftBadge>
              {p.data.isCurrentAgreement ? (
                <SoftBadge tone="green">Current</SoftBadge>
              ) : null}
            </Flx>
          ) : null,
      },
      {
        field: "status",
        headerName: "Status",
        width: 150,
        cellRenderer: (p) =>
          p.data?.status ? (
            <Flx ac sx={{ height: "100%" }}>
              <StatusChip status={p.data.status} />
            </Flx>
          ) : null,
      },
      { field: "agreementStage", headerName: "Stage", width: 130 },
      {
        field: "size",
        headerName: "Size",
        width: 100,
        valueFormatter: (p) => (p.data ? fmtBytes(p.value) : ""),
      },
      {
        field: "uploadDate",
        headerName: "Uploaded",
        width: 120,
        sort: "desc",
        valueFormatter: (p) =>
          p.data ? formatDateTime(p.value, "M/d/yy") || "—" : "",
      },
      { field: "uploadedBy", headerName: "Uploaded by", flex: 1, minWidth: 180 },
      actionColumn({
        count: 4,
        cellRenderer: (p) =>
          p.data ? (
            <ActionCell>
              <Tooltip title="Preview">
                <IconButton
                  aria-label="Preview"
                  onClick={(e) => {
                    e.stopPropagation();
                    h.current.onPreview(p.data);
                  }}
                >
                  <VisibilityOutlined sx={{ fontSize: 17 }} />
                </IconButton>
              </Tooltip>
              <Tooltip title="Download">
                <IconButton
                  aria-label="Download"
                  onClick={(e) => {
                    e.stopPropagation();
                    h.current.onDownload(p.data);
                  }}
                >
                  <FileDownloadOutlined sx={{ fontSize: 17 }} />
                </IconButton>
              </Tooltip>
              {isAgreementCategory(p.data._category) ? (
                <Tooltip title="Compare against this version">
                  <IconButton
                    aria-label="Compare"
                    onClick={(e) => {
                      e.stopPropagation();
                      h.current.onCompare(p.data);
                    }}
                  >
                    <CompareArrowsRounded sx={{ fontSize: 17 }} />
                  </IconButton>
                </Tooltip>
              ) : (
                <Box />
              )}
              {isAgreementCategory(p.data._category) ? (
                <StatusMenuButton doc={p.data} onPick={(d, st) => h.current.onStatus(d, st)} />
              ) : (
                <Box />
              )}
            </ActionCell>
          ) : null,
      }),
    ],
    [],
  );

  const groupRowParams = useMemo(
    () => ({
      suppressCount: true,
      innerRenderer: (p) => (
        <TableGroupRow node={p.node} onUpload={(c) => h.current.onUpload(c)} />
      ),
    }),
    [],
  );

  return (
    <AgGridTableWrapper>
      <AgGridReact
        rowData={rows}
        columnDefs={columnDefs}
        getRowId={(p) => String(oidOf(p.data) || p.data._name)}
        groupDisplayType="groupRows"
        groupDefaultExpanded={-1}
        groupRowRendererParams={groupRowParams}
        quickFilterText={term}
        domLayout="autoHeight"
        defaultColDef={{ sortable: true, resizable: true }}
        overlayNoRowsTemplate="No files uploaded yet"
      />
    </AgGridTableWrapper>
  );
}

export default function CorrespondentDocumentsCoverage({ detail, onChanged }) {
  const correspondentsApi = useCorrespondentsApi();
  const cid = docId(detail);

  const [term, setTerm] = useState("");
  // Board (every category, grouped cards) or Table (every file, grouped by
  // type) — a presentation switch, so a level-3 segmented toggle.
  const [view, setView] = useState("table");
  const [collapsed, setCollapsed] = useState(() => new Set());
  // Agreement categories open by default so versions are one glance away.
  const [openRows, setOpenRows] = useState(() => new Set(AGREEMENT_CATEGORIES));
  const [error, setError] = useState(null);
  const [previewDoc, setPreviewDoc] = useState(null);
  // The upload sidebar: null = closed, { category } = open (category may be
  // null from the header button — the panel asks for it).
  const [panel, setPanel] = useState(null);
  // Table view's compare: { category, from } (null = closed). The board keeps
  // its compare inside each agreement row.
  const [tableCompare, setTableCompare] = useState(null);
  // The upload response carries the updated documents — used until the
  // refreshed record arrives so the new file shows at once.
  const [uploadedDocs, setUploadedDocs] = useState(null);
  useEffect(() => setUploadedDocs(null), [detail]);
  const docs = useMemo(
    () => (uploadedDocs ?? detail?.documents ?? []).filter(Boolean),
    [uploadedDocs, detail],
  );


  // category → files (newest / highest version first)
  const byCategory = useMemo(() => {
    const m = {};
    docs.forEach((d) => {
      const c = canonCategory(d.category || "Other");
      (m[c] = m[c] || []).push(d);
    });
    Object.values(m).forEach((list) => list.sort(byNewest));
    return m;
  }, [docs]);

  // Groups with every category, plus any unknown category under Other —
  // groups and the types inside each listed A–Z.
  const groups = useMemo(() => {
    const extra = Object.keys(byCategory).filter((c) => !GROUPED.has(c));
    const az = (x, y) => x.localeCompare(y);
    return GROUPS.map((g) => ({
      ...g,
      categories: [...(g.name === "Other" ? extra : []), ...g.categories].sort(az),
    })).sort((x, y) => az(x.name, y.name));
  }, [byCategory]);

  const needle = term.trim().toLowerCase();
  const visibleGroups = groups
    .map((g) => ({
      group: g,
      rows: g.categories
        .map((c) => ({ category: c, files: byCategory[c] || [] }))
        .filter(
          (r) =>
            !needle ||
            r.category.toLowerCase().includes(needle) ||
            g.name.toLowerCase().includes(needle) ||
            r.files.some((f) => nameOf(f).toLowerCase().includes(needle)),
        ),
    }))
    .filter((g) => !needle || g.rows.length);

  const toggleSet = (setter) => (key) =>
    setter((s) => {
      const n = new Set(s);
      if (n.has(key)) n.delete(key);
      else n.add(key);
      return n;
    });
  const toggleGroup = toggleSet(setCollapsed);
  const toggleRow = toggleSet(setOpenRows);
  // Every upload — a row's Upload or the header's Upload version — opens the
  // same upload sidebar, with the row's category preselected.
  const upload = (category, file) =>
    setPanel({ category: category || null, file: file || null });

  // The download route streams bytes; pull them as a blob and save.
  const downloadDoc = async (doc) => {
    const oid = oidOf(doc);
    if (!oid) return;
    try {
      const blob = await correspondentsApi.downloadDocument(cid, oid);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = nameOf(doc);
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || "Download failed");
    }
  };

  // "Final Executed" promotes the version to the agreement of record and
  // stamps mlpaSigned — the record refresh carries both back.
  const setStatus = async (doc, status) => {
    setError(null);
    try {
      await correspondentsApi.setDocumentStatus(cid, oidOf(doc), { status });
      setUploadedDocs(null);
      onChanged?.();
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || "Status update failed");
    }
  };

  const renderCard = ({ group, rows }) => (
    <GroupCard
      key={group.name}
      group={group}
      rows={rows}
      term={needle}
      collapsed={collapsed.has(group.name)}
      onToggleCollapsed={() => toggleGroup(group.name)}
      renderRow={(r) => (
        <CategoryRow
          key={r.category}
          cid={cid}
          category={r.category}
          files={r.files}
          term={needle}
          open={openRows.has(r.category) || (!!needle && r.files.length > 0)}
          onToggle={() => toggleRow(r.category)}
          onUpload={upload}
          uploading={null}
          onPreview={setPreviewDoc}
          onDownload={downloadDoc}
          onStatus={setStatus}
        />
      )}
    />
  );

  const previewVersions = previewDoc
    ? byCategory[canonCategory(previewDoc.category || "Other")] || []
    : [];

  return (
    <Box>
      {/* ---- header: title, then search + actions ---- */}
      <Box sx={{ mb: 2, fontSize: 21, fontWeight: 700, letterSpacing: "-0.02em", color: slate[900] }}>
        Agreement Documents
      </Box>

      <Flx ac wrap g={1.25} sx={{ pb: 1.5, borderBottom: `1px solid ${LINE_SOFT}` }}>
        <TextField
          size="small"
          value={term}
          onChange={(e) => setTerm(e.target.value)}
          placeholder="Search document types or files"
          sx={{
            width: 320,
            maxWidth: "100%",
            "& .MuiOutlinedInput-root": { background: "#fff", borderRadius: "8px", fontSize: 13 },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchOutlined sx={{ fontSize: 18, color: INK_3 }} />
              </InputAdornment>
            ),
            endAdornment: term ? (
              <InputAdornment position="end">
                <IconButton size="small" aria-label="Clear search" onClick={() => setTerm("")}>
                  <CloseRounded sx={{ fontSize: 16 }} />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
        />
        <ToggleButtonGroup
          exclusive
          size="small"
          value={view}
          onChange={(_, v) => v && setView(v)}
          aria-label="View"
          sx={{ ...segmentedGroupSx(), border: `1px solid ${LINE}` }}
        >
          <ToggleButton value="table">Uploaded</ToggleButton>
          <ToggleButton value="board">All Types</ToggleButton>
        </ToggleButtonGroup>
        <Flx ac g={1} sx={{ ml: "auto" }}>
          {/* Blank, most-recent fillable forms live in Box — nothing is stored
              as a template. */}
          <Button
            variant="outlined"
            component="a"
            href={ONBOARDING_BLANK_FORMS_URL}
            target="_blank"
            rel="noopener noreferrer"
            endIcon={<OpenInNewRounded sx={{ fontSize: 15 }} />}
            sx={HEADER_BTN}
          >
            Blank Forms
          </Button>
          <Button
            variant="contained"
            startIcon={<UploadFileOutlined sx={{ fontSize: 17 }} />}
            sx={HEADER_BTN}
            // Opens on the MLPA, as the tab always did — the panel's
            // Agreement (stage / supersedes) section only shows once an
            // agreement category is picked.
            onClick={() => upload(AGREEMENT_CATEGORIES[0])}
          >
            Upload Version
          </Button>
        </Flx>
      </Flx>

      {error && (
        <Box sx={{ mt: 1.5, fontSize: 12.5, color: statusColors.error.fg }}>
          {error}
        </Box>
      )}

      <DropToUpload onFile={(f) => upload(null, f)} />

      {/* ---- Uploaded (table, the default) or All Types (the board: one
           group card per row) ---- */}
      {view === "table" ? (
        <Box sx={{ mt: 2 }}>
          {tableCompare && (
            <Box sx={{ mb: 2 }}>
              <ComparePanel
                cid={cid}
                category={tableCompare.category}
                versions={byCategory[tableCompare.category] || []}
                from={tableCompare.from}
                onFromChange={(from) => setTableCompare((c) => (c ? { ...c, from } : c))}
                onClose={() => setTableCompare(null)}
              />
            </Box>
          )}
          <DocumentsTable
            docs={docs}
            term={term}
            onPreview={setPreviewDoc}
            onDownload={downloadDoc}
            onStatus={setStatus}
            onUpload={(c) => upload(c)}
            onCompare={(d) =>
              setTableCompare({
                category: canonCategory(d.category || "Other"),
                from: oidOf(d),
              })
            }
          />
        </Box>
      ) : visibleGroups.length ? (
        <Box
          sx={{
            mt: 2,
            display: "grid",
            gridTemplateColumns: "minmax(0, 1fr)",
            gap: "14px",
            alignItems: "start",
            // the cards carry a bottom margin for the old masonry layout
            "& > *": { mb: "0 !important" },
          }}
        >
          {visibleGroups.map(renderCard)}
        </Box>
      ) : (
        <Flx column ac sx={{ py: 7.5, px: 2.5, textAlign: "center" }}>
          <Box sx={{ fontSize: 15, fontWeight: 600, color: slate[900], mb: 0.5 }}>No matches</Box>
          <Box sx={{ fontSize: 13, color: INK_2 }}>Try a different search term.</Box>
        </Flx>
      )}

      <AgreementDocumentModal
        open={!!previewDoc}
        onClose={() => setPreviewDoc(null)}
        doc={previewDoc}
        companyId={cid}
        versions={previewVersions}
      />

      {panel && (
        <CorrespondentUploadPanel
          cid={cid}
          defaultCategory={panel.category}
          initialFile={panel.file}
          // The panel's "supersedes" picker lists these — the row's own
          // versions, or every agreement version from the header button.
          versions={
            panel.category
              ? byCategory[panel.category] || []
              : AGREEMENT_CATEGORIES.flatMap((c) => byCategory[c] || [])
          }
          subtitle={detail?.companyName}
          onClose={() => setPanel(null)}
          onUploaded={(res) => {
            const next = (res?.documents ?? []).filter(Boolean);
            if (next.length) setUploadedDocs(next);
            const added = next.find(
              (d) => !docs.some((o) => oidOf(o) === oidOf(d)),
            );
            const cat = canonCategory(added?.category || panel.category || "");
            if (cat) setOpenRows((s) => new Set(s).add(cat));
            setPanel(null);
            onChanged?.();
          }}
        />
      )}
    </Box>
  );
}

// Every backend category must have a home in GROUPS (dev-time guard).
if (process.env.NODE_ENV !== "production") {
  const missing = DOCUMENT_CATEGORIES.filter((c) => !GROUPED.has(c));
  if (missing.length)
    // eslint-disable-next-line no-console
    console.warn("CorrespondentDocumentsCoverage: ungrouped categories", missing);
}
